'use client';
import React from 'react';
import Link from 'next/link';
import { Wallet, Trophy, Target, Zap, BarChart3, Flame, AlertTriangle, TrendingUp } from 'lucide-react';

const kpiCards = [
  {
    id: 'kpi-wallet',
    label: 'Available Balance',
    value: '$124.50',
    sublabel: '+$45.00 today',
    icon: Wallet,
    color: '#7C3AED',
    bg: 'rgba(124,58,237,0.12)',
    trend: 'up',
    trendValue: '+56%',
    href: '/wallet-screen',
    span: 'col-span-1 md:col-span-2',
    isHero: true,
  },
  {
    id: 'kpi-active',
    label: 'Active Competitions',
    value: '3',
    sublabel: '2 ending soon',
    icon: Trophy,
    color: '#F59E0B',
    bg: 'rgba(245,158,11,0.1)',
    trend: 'neutral',
    trendValue: '1 new',
    href: '/discover-screen',
    span: 'col-span-1',
    isAlert: true,
  },
  {
    id: 'kpi-winrate',
    label: 'Win Rate',
    value: '67.4%',
    sublabel: 'Last 30 days',
    icon: Target,
    color: '#10B981',
    bg: 'rgba(16,185,129,0.1)',
    trend: 'up',
    trendValue: '+4.2%',
    href: '/user-dashboard',
    span: 'col-span-1',
    isAlert: false,
  },
  {
    id: 'kpi-xp',
    label: 'XP & Level',
    value: '2,840',
    sublabel: 'Level 14 · Gold',
    icon: Zap,
    color: '#F59E0B',
    bg: 'rgba(245,158,11,0.1)',
    trend: 'up',
    trendValue: '+320 XP',
    href: '/user-dashboard',
    span: 'col-span-1',
    xpProgress: 68,
  },
  {
    id: 'kpi-rank',
    label: 'Global Rank',
    value: '#847',
    sublabel: 'Top 2.3%',
    icon: BarChart3,
    color: '#06B6D4',
    bg: 'rgba(6,182,212,0.1)',
    trend: 'up',
    trendValue: '+12 positions',
    href: '/user-dashboard',
    span: 'col-span-1',
  },
  {
    id: 'kpi-streak',
    label: 'Daily Streak',
    value: '14',
    sublabel: 'days in a row',
    icon: Flame,
    color: '#F43F5E',
    bg: 'rgba(244,63,94,0.1)',
    trend: 'up',
    trendValue: 'Personal best!',
    href: '/user-dashboard',
    span: 'col-span-1',
  },
  {
    id: 'kpi-prizes',
    label: "Today\'s Prize Pool",
    value: '$42,800',
    sublabel: '127 competitions',
    icon: TrendingUp,
    color: '#A78BFA',
    bg: 'rgba(167,139,250,0.1)',
    trend: 'neutral',
    trendValue: 'Join now',
    href: '/discover-screen',
    span: 'col-span-1',
  },
];

export default function DashboardKPIGrid() {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
      {kpiCards?.map((card) => (
        <Link
          key={card?.id}
          href={card?.href}
          className={`card-hover rounded-xl p-5 block ${card?.span}`}
          style={{
            background: card?.isHero
              ? `linear-gradient(135deg, rgba(124,58,237,0.2), rgba(124,58,237,0.05))`
              : 'var(--card)',
            border: card?.isAlert
              ? '1px solid rgba(245,158,11,0.35)'
              : card?.isHero
                ? '1px solid rgba(124,58,237,0.35)'
                : '1px solid var(--border)',
          }}
        >
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ background: card?.bg }}>
                <card.icon size={16} style={{ color: card?.color }} />
              </div>
              {card?.isAlert && (
                <AlertTriangle size={14} style={{ color: '#F59E0B' }} />
              )}
            </div>
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full"
              style={{
                background: card?.trend === 'up' ? 'rgba(16,185,129,0.15)' : card?.trend === 'down' ? 'rgba(244,63,94,0.15)' : 'rgba(100,116,139,0.15)',
                color: card?.trend === 'up' ? '#10B981' : card?.trend === 'down' ? '#F43F5E' : '#94A3B8',
              }}>
              {card?.trendValue}
            </span>
          </div>

          <p className="metric-label mb-1">{card?.label}</p>
          <p className="metric-value" style={{ color: card?.isHero ? '#A78BFA' : 'var(--foreground)', fontSize: card?.isHero ? '32px' : '26px' }}>
            {card?.value}
          </p>
          <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>{card?.sublabel}</p>

          {card?.xpProgress !== undefined && (
            <div className="mt-3">
              <div className="flex justify-between text-xs mb-1" style={{ color: 'var(--muted-foreground)' }}>
                <span>Level 14</span>
                <span>{card?.xpProgress}% to Lv.15</span>
              </div>
              <div className="progress-xp">
                <div className="progress-xp-fill" style={{ width: `${card?.xpProgress}%` }} />
              </div>
            </div>
          )}
        </Link>
      ))}
    </div>
  );
}